import React, { useContext, useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import "./Header.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFacebook,
  faInstagram,
  faLinkedin,
} from "@fortawesome/free-brands-svg-icons";
import {
  faUser,
  faGlobe,
  faArrowRightFromBracket,
  faEnvelope,
  faPhone,
  faLocationDot,
  faBars,
  faSearch,
  faTimes,
  faUtensils,
  faBuilding,
  faHandsWash,
  faTshirt,
  faSeedling,
  faSwimmingPool,
  faFlask,
  faTools,
  faPlusCircle,
  faMinusCircle,
  faUpload,
  faTrashAlt,
  faFileUpload,
  faTrash,
  faSort,
  faSprayCan,
} from "@fortawesome/free-solid-svg-icons";
import { AuthContext } from "../../authContetxt"; // Ensure the path is correct
import { Link as RouterLink, useNavigate } from "react-router-dom";
import axios from "../../utils/axiosConfig"; // Ensure your axios configuration is correct
import { useTranslation, getI18n } from "react-i18next";
import { useLanguage } from "../../contexts/LanguageContext";
import useLocalizedPath from "../../hooks/useLocalizedPath";
import { isPublicPath } from "../../utils/languageUrl";

const Link = ({ to, ...props }) => {
  const localizedPath = useLocalizedPath();
  const pathname = new URL(to, "https://krlkimya.com").pathname;
  const destination = isPublicPath(pathname) ? localizedPath(to) : to;

  return <RouterLink to={destination} {...props} />;
};

const Header = () => {
  const location = useLocation();
  const searchResultsRef = useRef(null);
  const { authToken, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentLanguage, toggleLanguage, isEnglish } = useLanguage();
  const localizedPath = useLocalizedPath();

  // State to manage the navigation menu's open/close status
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [openDesktopSubMenu, setOpenDesktopSubMenu] = useState(null);
  const [openMobileHeaderSubMenu, setOpenMobileHeaderSubMenu] = useState(null);

  // Separate state variables for mobile submenus
  const [isProductsSubMenuOpen, setProductsSubMenuOpen] = useState(false);
    // Close any open mobile submenus on navigation
  useEffect(() => {
   setOpenMobileHeaderSubMenu(null);
   setProductsSubMenuOpen(false);
  }, [location]);


  const [isAdminSubMenuOpen, setAdminSubMenuOpen] = useState(false);

  // Function to handle clicks outside the search results
  const handleClickOutside = (event) => {
    if (
      searchResultsRef.current &&
      !searchResultsRef.current.contains(event.target)
    ) {
      setSearchResults([]);
    }
  };

  // Function to toggle the navigation menu
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!isMobileMenuOpen);
  };

  const openFacebook = () => {
    window.open("https://www.facebook.com/krlkimya", "_blank");
  };

  const openInstagram = () => {
    window.open("https://www.instagram.com/krlkimya", "_blank");
  };

  const openLinkedIn = () => {
    window.open("https://www.linkedin.com/company/krl-kimya", "_blank");
  };

  const openContact = () => {
    navigate(localizedPath("/contact"));
  };

  const openLogin = () => {
    navigate("/login");
  };

  const handleLogout = () => {
    logout();
    setShowPopup(true);
    setTimeout(() => {
      setShowPopup(false);
    }, 3000);
  };

  // Function to fetch search results
  const fetchSearchResults = async (query) => {
    try {
      let searchResults = [];
      
      if (currentLanguage === 'en') {
        // For English language, use SMART search logic
        try {
          // First, try to find English products (native English content)
          let englishProducts = [];
          try {
            const englishResponse = await axios.get(`/api/english-products/search?query=${query}`);
            englishProducts = englishResponse.data || [];
          } catch (englishErr) {
            // 404 or any error -> treat as no English results and fall back
            englishProducts = [];
          }

          if (englishProducts.length > 0) {
            // If we found English products, use them and don't search Turkish products
            searchResults = englishProducts.map(product => ({
              ...product,
              isEnglishProduct: true,
              hasManualTranslation: false
            }));

            
          } else {
            // If no English products found, search Turkish products with manual translations

            let turkishProducts = [];
            try {
              const turkishResponse = await axios.get(`/api/products/search?query=${query}`);
              turkishProducts = turkishResponse.data || [];
            } catch (trErr) {
              turkishProducts = [];
            }

            // Filter Turkish products to only include those with manual English translations
            const turkishProductsWithTranslations = turkishProducts.filter(product => {
              const hasTranslation = hasManualEnglishTranslation(product._id);
              return hasTranslation;
            });

            // Mark Turkish products with translations
            searchResults = turkishProductsWithTranslations.map(product => ({
              ...product,
              isEnglishProduct: false,
              hasManualTranslation: true
            }));
          }

        } catch (error) {
          searchResults = [];
        }
      } else {
        // For Turkish language, ONLY search Turkish products
        try {
          const response = await axios.get(`/api/products/search?query=${query}`);
          const turkishProducts = response.data || [];
          
          // Mark all as Turkish products
          searchResults = turkishProducts.map(product => ({ 
            ...product, 
            isEnglishProduct: false 
          }));
        } catch (error) {
          searchResults = [];
        }
      }
      
      setSearchResults(searchResults);
    } catch (error) {
      setSearchResults([]); // Clear results on error
    }
  };

  // Add event listener once on mount
  useEffect(() => {
    document.addEventListener("click", handleClickOutside); // Changed from 'mousedown' to 'click'
    // Cleanup event listener on unmount
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  // Clear search results when language changes
  useEffect(() => {
    setSearchResults([]);
    setSearchTerm("");
  }, [currentLanguage]);

  // Handle search term changes
  useEffect(() => {
    if (searchTerm.trim() !== "") {
      const delayDebounceFn = setTimeout(() => {
        fetchSearchResults(searchTerm);
      }, 300); // Debounce for 300ms
      return () => clearTimeout(delayDebounceFn);
    } else {
      setSearchResults([]);
    }
  }, [searchTerm, currentLanguage]); // Added currentLanguage dependency

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleProductClick = (productId, event) => {
    event.stopPropagation(); // Prevent the click event from propagating
    navigate(localizedPath(`/products/${productId}`)); // Navigate to the product detail page
    setSearchResults([]); // Clear search results
    setSearchTerm(""); // Clear search term
    setMobileMenuOpen(false); // Close the mobile menu if it's open
  };

  const clearSearch = () => {
    setSearchTerm("");
    setSearchResults([]);
  };

  // Helper function to check if a Turkish product has manual English translation
  const hasManualEnglishTranslation = (productId) => {
    try {
      // Always check English translations regardless of current language
      const i18nInstance = getI18n();
      const englishTranslations = i18nInstance.getResourceBundle('en', 'translation');
      
      if (!englishTranslations || !englishTranslations.product_items) {
        return false;
      }
      
      // Check if the product has an English translation
      const productTranslation = englishTranslations.product_items[productId];
      const hasTranslation = productTranslation && productTranslation.title && productTranslation.title.trim() !== '';
      return hasTranslation;
    } catch (error) {
      return false;
    }
  };

  // Helper function to get display text based on language
  const getDisplayText = (product) => {
    if (currentLanguage === 'en') {
      if (product.isEnglishProduct) {
        // This is an English product, show English content
        return {
          title: product.title || '',
          description: product.description || ''
        };
      } else if (product.hasManualTranslation) {
        // This is a Turkish product with manual English translation in en.json
        // We need to get the English translation from the translation file
        const productId = product._id;
        const englishTranslation = t(`product_items.${productId}.title`, product.title);
        const englishDescription = t(`product_items.${productId}.description`, product.description);
        
        return {
          title: englishTranslation,
          description: englishDescription
        };
      } else {
        // Fallback to Turkish content if no translation available
        return {
          title: product.title || '',
          description: product.description || ''
        };
      }
    } else {
      // For Turkish language, always show Turkish content
      return {
        title: product.title || '',
        description: product.description || ''
      };
    }
  };

  // Helper function to construct image URL
  const constructImageUrl = (baseFilename, size = "320w", format = "png") => {
    return `https://krl-kimya.fra1.digitaloceanspaces.com/images/${baseFilename}-${size}.${format}`;
  };

  return (
    <header className="header-top-two">
      {/* Header Top */}
      <div className="header-top header-top-two">
        <div className="container header-top-container">
          <div className="header-top-left">
            <span onClick={openFacebook}>
              <FontAwesomeIcon icon={faFacebook} className="header-icon" />
              <span className="social-text">facebook</span>
            </span>
            <span onClick={openInstagram}>
              <FontAwesomeIcon icon={faInstagram} className="header-icon" />
              <span className="social-text">instagram</span>
            </span>
            <span onClick={openLinkedIn}>
              <FontAwesomeIcon icon={faLinkedin} className="header-icon" />
              <span className="social-text">linkedIn</span>
            </span>
          </div>
          <div className="header-top-right">
            <button className="demo-button" onClick={openContact}>
              {t('header.demo_button')}
            </button>
            <button
              className="lang-button"
              onClick={toggleLanguage}
              title={isEnglish ? "Türkçe'ye geç" : "Switch to English"}
            >
              <FontAwesomeIcon
                icon={faGlobe}
                className="lang-globe-icon"
              />
              {isEnglish ? "TR" : "EN"}
            </button>
            {authToken ? (
              <FontAwesomeIcon
                icon={faArrowRightFromBracket}
                className="login-icon"
                onClick={handleLogout}
                title="Logout"
              />
            ) : (
              <FontAwesomeIcon
                icon={faUser}
                className="login-icon"
                onClick={openLogin}
                title="Login"
              />
            )}
          </div>
        </div>
      </div>

      {/* Header Logo and Contact Info */}
      <div className="header-logo-area">
        <div className="container header-logo-container">
          <div className="logo">
            <Link to="/">
              <img src="/images/krl_logo.png" alt="Logo" />
            </Link>
          </div>
          <div className="header-contact-info">
            <div className="contact-info-item">
              <div className="icon">
                <FontAwesomeIcon icon={faEnvelope} size="2x" />
              </div>
              <div className="content">
                <h4 className="title">{t('header.contact.write_to_us')}</h4>
                <a href="mailto:info@krlkimya.com">info@krlkimya.com</a>
              </div>
            </div>
            <div className="contact-info-item">
              <div className="icon">
                <FontAwesomeIcon icon={faPhone} size="2x" />
              </div>
              <div className="content">
                <h4 className="title">{t('header.contact.phone_number')}</h4>
                <a href="tel:+90 549 575 44 08">+90 549 575 44 08</a>
              </div>
            </div>
            <div className="contact-info-item">
              <div className="icon">
                <FontAwesomeIcon icon={faLocationDot} size="2x" />
              </div>
              <div className="content">
                <h4 className="title">{t('header.contact.company_name')}</h4>
                <a
                  href="https://maps.app.goo.gl/Jk8ef54a4V83cGyB8"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('header.contact.address_short')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="menu-area">
        <div className="container menu-container">
          <nav className="menu-nav">
            {/* Mobile Header */}
            <div className="mobile-header">
              <div className="mobile-menu-icon" onClick={toggleMobileMenu}>
                <FontAwesomeIcon icon={faBars} size="lg" />
              </div>
              <div className="mobile-menu-items">
                <Link to="/about" className="mobile-menu-link">
                  {t('header.navigation.about')}
                </Link>
                <div
                  className="mobile-menu-link menu-item-has-children"
                  style={{ position: "relative" }}
                >
                  <span
                    className="menu-title"
                    onClick={() => {
                      if (openMobileHeaderSubMenu === "productsMobileHeader") {
                        setOpenMobileHeaderSubMenu(null);
                      } else {
                        setOpenMobileHeaderSubMenu("productsMobileHeader");
                      }
                    }}
                  >
                    {t('header.navigation.products')}
                  </span>
                  {openMobileHeaderSubMenu === "productsMobileHeader" && (
                    <ul className="sub-menu sub-menu-mobile-header">
                      <li>
                        <Link to="/products?category=mutfak">
                          <FontAwesomeIcon
                            icon={faUtensils}
                            className="submenu-icon"
                          />
                          {t('products.categories.kitchen')}
                        </Link>
                      </li>
                      <li>
                        <Link to="/products?category=bina-bakimi">
                          <FontAwesomeIcon
                            icon={faBuilding}
                            className="submenu-icon"
                          />
                          {t('products.categories.building_maintenance')}
                        </Link>
                      </li>
                      <li>
                        <Link to="/products?category=kisisel-hijyen">
                          <FontAwesomeIcon
                            icon={faHandsWash}
                            className="submenu-icon"
                          />
                          {t('products.categories.personal_hygiene')}
                        </Link>
                      </li>
                      <li>
                        <Link to="/products?category=camasir">
                          <FontAwesomeIcon
                            icon={faTshirt}
                            className="submenu-icon"
                          />
                          {t('products.categories.laundry')}
                        </Link>
                      </li>
                      <li>
                        <Link to="/products?category=gida-hijyeni">
                          <FontAwesomeIcon
                            icon={faSeedling}
                            className="submenu-icon"
                          />
                          {t('products.categories.food_hygiene')}
                        </Link>
                      </li>
                      <li>
                        <Link to="/products?category=dezenfektanlar-ve-antiseptikler">
                          <FontAwesomeIcon
                            icon={faSprayCan}
                            className="submenu-icon"
                          />
                          {t('products.categories.disinfectants')}
                        </Link>
                      </li>
                      <li>
                        <Link to="/products?category=ozel-urunler">
                          <FontAwesomeIcon
                            icon={faFlask}
                            className="submenu-icon"
                          />
                          {t('products.categories.special_products')}
                        </Link>
                      </li>
                      <li>
                        <Link to="/products?category=endustriyel-kimya">
                          <FontAwesomeIcon
                            icon={faTools}
                            className="submenu-icon"
                          />
                          {t('products.categories.industrial_chemistry')}
                        </Link>
                      </li>
                    </ul>
                  )}
                </div>
                <Link to="/contact" className="mobile-menu-link">
                  {t('header.navigation.contact')}
                </Link>
              </div>
            </div>

            {/* Mobile Menu Overlay */}
            {isMobileMenuOpen && (
              <>
                <div
                  className="mobile-menu-overlay"
                  onClick={toggleMobileMenu}
                ></div>
                <div
                  className={`mobile-menu ${isMobileMenuOpen ? "open" : ""}`}
                >
                 {/* Close Icon & Logo */}
                 <div className="mobile-menu-close" onClick={toggleMobileMenu}>
                    <img
                      src="/images/krl_logo.png"
                      alt="KRL Logo"
                      
                      style={{ width: "120px", marginRight: "80px" }}
                    />
                    <FontAwesomeIcon icon={faTimes} />
                  </div>
                  {/* Mobile Search Bar */}
                  <div className="mobile-search-container">
                    <div className="search-container-mobile">
                      <FontAwesomeIcon
                        icon={faSearch}
                        className="search-icon-mobile"
                      />
                      <input
                        key={`mobile-search-${currentLanguage}`} // Force re-render when language changes
                        type="text"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        placeholder={t('header.search.placeholder')}
                        className="search-input-mobile"
                      />
                      {searchTerm && (
                        <FontAwesomeIcon
                          icon={faTimes}
                          className="cancel-icon-mobile"
                          onClick={clearSearch}
                        />
                      )}
                    </div>
                    {/* Display Search Results */}
                    {searchResults.length > 0 && (
                      <div
                        className="search-results-mobile"
                        ref={searchResultsRef}
                      >
                        {searchResults
                          .filter(product => {
                            // For English language, only show English products or Turkish products with translations
                            if (currentLanguage === 'en') {
                              return product.isEnglishProduct || product.hasEnglishTranslation;
                            }
                            // For Turkish language, show all Turkish products
                            return true;
                          })
                          .map((product) => {
                          // Handle cases where product.images[0] is a full URL or a base filename
                          let imageUrl = "";
                          const imageEntry = product.images[0];

                          if (imageEntry.startsWith("http")) {
                            imageUrl = imageEntry;
                          } else {
                            imageUrl = constructImageUrl(imageEntry);
                          }

                          const { title, description } = getDisplayText(product);

                          return (
                            <div
                              key={product._id}
                              className="search-result-item"
                              onClick={(e) =>
                                handleProductClick(product._id, e)
                              }
                              style={{ cursor: "pointer" }}
                            >
                              <img
                                src={imageUrl}
                                alt={title}
                                className="search-result-image"
                              />
                              <div>
                                <h4>{title}</h4>
                                <p>{description}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {/* Mobile Menu Items */}
                  <ul className="mobile-navigation">
                    <li>
                      <Link to="/" onClick={toggleMobileMenu}>
                        {t('header.navigation.home')}
                      </Link>
                    </li>
                    <li>
                      <Link to="/about" onClick={toggleMobileMenu}>
                        {t('header.navigation.about')}
                      </Link>
                    </li>
                    <li
                      className={`menu-item-has-children ${
                        isProductsSubMenuOpen ? "open" : ""
                      }`}
                    >
                      <span
                        className="menu-title"
                        onClick={() => {
                          setProductsSubMenuOpen(!isProductsSubMenuOpen);
                        }}
                      >
                        {t('header.navigation.products')}
                      </span>
                      {isProductsSubMenuOpen && (
                        <ul className="sub-menu">
                          <li>
                            <Link
                              to="/products?category=mutfak"
                              onClick={toggleMobileMenu}
                            >
                              <FontAwesomeIcon
                                icon={faUtensils}
                                className="submenu-icon"
                              />
                              {t('products.categories.kitchen')}
                            </Link>
                          </li>
                          <li>
                            <Link to="/products?category=bina-bakimi"
                             onClick={toggleMobileMenu}>
                            
                              <FontAwesomeIcon
                                icon={faBuilding}
                                className="submenu-icon"
                              />
                              {t('products.categories.building_maintenance')}
                            </Link>
                          </li>
                          <li>
                            <Link to="/products?category=kisisel-hijyen"
                            onClick={toggleMobileMenu}>
                              <FontAwesomeIcon
                                icon={faHandsWash}
                                className="submenu-icon"
                              />
                              {t('products.categories.personal_hygiene')}
                            </Link>
                          </li>
                          <li>
                            <Link to="/products?category=camasir"
                            onClick={toggleMobileMenu}>
                              <FontAwesomeIcon
                                icon={faTshirt}
                                className="submenu-icon"
                              />
                              {t('products.categories.laundry')}
                            </Link>
                          </li>
                          <li>
                            <Link to="/products?category=gida-hijyeni"
                            onClick={toggleMobileMenu}>
                              <FontAwesomeIcon
                                icon={faSeedling}
                                className="submenu-icon"
                              />
                              {t('products.categories.food_hygiene')}
                            </Link>
                          </li>
                          <li>
                            <Link to="/products?category=dezenfektanlar-ve-antiseptikler"
                            onClick={toggleMobileMenu}>
                              <FontAwesomeIcon
                                icon={faSprayCan}
                                className="submenu-icon"
                              />
                              {t('products.categories.disinfectants')}
                            </Link>
                          </li>
                          <li>
                            <Link to="/products?category=ozel-urunler"
                            onClick={toggleMobileMenu}>
                              <FontAwesomeIcon
                                icon={faFlask}
                                className="submenu-icon"
                              />
                              {t('products.categories.special_products')}
                            </Link>
                          </li>
                          <li>
                            <Link to="/products?category=endustriyel-kimya"
                            onClick={toggleMobileMenu}>
                              <FontAwesomeIcon
                                icon={faTools}
                                className="submenu-icon"
                              />
                              {t('products.categories.industrial_chemistry')}
                            </Link>
                          </li>
                        </ul>
                      )}
                    </li>
                    <li>
                      <Link to="/catalog" onClick={toggleMobileMenu}>
                        {t('header.navigation.catalogs')}
                      </Link>
                    </li>
                    <li>
                      <Link to="/documents" onClick={toggleMobileMenu}>
                        {t('header.navigation.documents')}
                      </Link>
                    </li>
                    <li>
                      <Link to="/contact" onClick={toggleMobileMenu}>
                        {t('header.navigation.contact')}
                      </Link>
                    </li>
                    {authToken && (
                      <li
                        className={`menu-item-has-children ${
                          isAdminSubMenuOpen ? "open" : ""
                        }`}
                      >
                        <span
                          className="menu-title"
                          onClick={() => {
                            setAdminSubMenuOpen(!isAdminSubMenuOpen);
                          }}
                        >
                          {t('header.navigation.admin_platform')}
                        </span>
                        {isAdminSubMenuOpen && (
                          <ul className="sub-menu">
                            <li>
                              <Link
                                to="/add-product"
                                onClick={toggleMobileMenu}
                              >
                                <FontAwesomeIcon
                                  icon={faPlusCircle}
                                  className="submenu-icon"
                                />
                                {t('header.navigation.add_product')}
                              </Link>
                            </li>
                            <li>
                              <Link to="/manage-products">
                                <FontAwesomeIcon
                                  icon={faMinusCircle}
                                  className="submenu-icon"
                                />
                                {t('header.navigation.delete_product')}
                              </Link>
                            </li>
                            <li>
                              <Link to="/add-english-product">
                                <FontAwesomeIcon
                                  icon={faPlusCircle}
                                  className="submenu-icon"
                                />
                                Add English Product
                              </Link>
                            </li>
                            <li>
                              <Link to="/manage-english-products">
                                <FontAwesomeIcon
                                  icon={faMinusCircle}
                                  className="submenu-icon"
                                />
                                Manage English Products
                              </Link>
                            </li>
                            <li>
                              <Link to="/order-products">
                                <FontAwesomeIcon
                                  icon={faSort}
                                  className="submenu-icon"
                                />
                                {t('header.navigation.product_ordering')}
                              </Link>
                            </li>
                            <li>
                              <Link to="/add-catalog">
                                <FontAwesomeIcon
                                  icon={faFileUpload}
                                  className="submenu-icon"
                                />
                                {t('header.navigation.add_catalog')}
                              </Link>
                            </li>
                            <li>
                              <Link to="/manage-catalogs">
                                <FontAwesomeIcon
                                  icon={faTrashAlt}
                                  className="submenu-icon"
                                />
                                {t('header.navigation.delete_catalog')}
                              </Link>
                            </li>
                            <li>
                              <Link to="/add-documents">
                                <FontAwesomeIcon
                                  icon={faFileUpload}
                                  className="submenu-icon"
                                />
                                {t('header.navigation.add_document')}
                              </Link>
                            </li>
                            <li>
                              <Link to="/manage-documents">
                                <FontAwesomeIcon
                                  icon={faTrash}
                                  className="submenu-icon"
                                />
                                {t('header.navigation.delete_document')}
                              </Link>
                            </li>
                          </ul>
                        )}
                      </li>
                    )}
                  </ul>
                </div>
              </>
            )}

            {/* Desktop Navigation */}
            <ul className="navigation">
              <li>
                <Link to="/">{t('header.navigation.home')}</Link>
              </li>
              <li>
                <Link to="/about">{t('header.navigation.about')}</Link>
              </li>
              <li
                className="menu-item-has-children"
                onMouseEnter={() => setOpenDesktopSubMenu("products")}
                onMouseLeave={() => setOpenDesktopSubMenu(null)}
              >
                <span className="menu-title">{t('header.navigation.products')}</span>
                {openDesktopSubMenu === "products" && (
                  <ul className="sub-menu sub-menu-desktop">
                    <li>
                                              <Link to="/products?category=mutfak">
                          <FontAwesomeIcon
                            icon={faUtensils}
                            className="submenu-icon"
                          />
                          {t('products.categories.kitchen')}
                        </Link>
                    </li>
                    <li>
                                              <Link to="/products?category=bina-bakimi">
                          <FontAwesomeIcon
                            icon={faBuilding}
                            className="submenu-icon"
                          />
                          {t('products.categories.building_maintenance')}
                        </Link>
                    </li>
                    <li>
                                              <Link to="/products?category=kisisel-hijyen">
                          <FontAwesomeIcon
                            icon={faHandsWash}
                            className="submenu-icon"
                          />
                          {t('products.categories.personal_hygiene')}
                        </Link>
                    </li>
                    <li>
                                              <Link to="/products?category=camasir">
                          <FontAwesomeIcon
                            icon={faTshirt}
                            className="submenu-icon"
                          />
                          {t('products.categories.laundry')}
                        </Link>
                    </li>
                    <li>
                                              <Link to="/products?category=gida-hijyeni">
                          <FontAwesomeIcon
                            icon={faSeedling}
                            className="submenu-icon"
                          />
                          {t('products.categories.food_hygiene')}
                        </Link>
                    </li>
                    <li>
                                              <Link to="/products?category=dezenfektanlar-ve-antiseptikler">
                          <FontAwesomeIcon
                            icon={faSprayCan}
                            className="submenu-icon"
                          />
                          {t('products.categories.disinfectants')}
                        </Link>
                    </li>
                    <li>
                                              <Link to="/products?category=ozel-urunler">
                          <FontAwesomeIcon
                            icon={faFlask}
                            className="submenu-icon"
                          />
                          {t('products.categories.special_products')}
                        </Link>
                    </li>
                    <li>
                                              <Link to="/products?category=endustriyel-kimya">
                          <FontAwesomeIcon
                            icon={faTools}
                            className="submenu-icon"
                          />
                          {t('products.categories.industrial_chemistry')}
                        </Link>
                    </li>
                  </ul>
                )}
              </li>
              <li>
                <Link to="/catalog">{t('header.navigation.catalogs')}</Link>
              </li>
              <li>
                <Link to="/documents">{t('header.navigation.documents')}</Link>
              </li>
              <li>
                <Link to="/contact">{t('header.navigation.contact')}</Link>
              </li>
              {authToken && (
                <li
                  className="menu-item-has-children"
                  onMouseEnter={() => setOpenDesktopSubMenu("admin")}
                  onMouseLeave={() => setOpenDesktopSubMenu(null)}
                >
                  <span className="menu-title">{t('header.navigation.admin_platform')}</span>
                  {openDesktopSubMenu === "admin" && (
                    <ul className="sub-menu sub-menu-desktop">
                      <li>
                        <Link to="/add-product">
                          <FontAwesomeIcon
                            icon={faPlusCircle}
                            className="submenu-icon"
                          />
                          {t('header.navigation.add_product')}
                        </Link>
                      </li>
                      <li>
                        <Link to="/manage-products">
                          <FontAwesomeIcon
                            icon={faMinusCircle}
                            className="submenu-icon"
                          />
                          {t('header.navigation.delete_product')}
                        </Link>
                      </li>
                      <li>
                        <Link to="/add-english-product">
                          <FontAwesomeIcon
                            icon={faPlusCircle}
                            className="submenu-icon"
                          />
                          Add English Product
                        </Link>
                      </li>
                      <li>
                        <Link to="/manage-english-products">
                          <FontAwesomeIcon
                            icon={faMinusCircle}
                            className="submenu-icon"
                          />
                          Manage English Products
                        </Link>
                      </li>
                      <li>
                        <Link to="/order-products">
                          <FontAwesomeIcon
                            icon={faSort}
                            className="submenu-icon"
                          />
                          {t('header.navigation.product_ordering')}
                        </Link>
                      </li>
                      <li>
                        <Link to="/add-catalog">
                          <FontAwesomeIcon
                            icon={faUpload}
                            className="submenu-icon"
                          />
                          {t('header.navigation.add_catalog')}
                        </Link>
                      </li>
                      <li>
                        <Link to="/manage-catalogs">
                          <FontAwesomeIcon
                            icon={faTrashAlt}
                            className="submenu-icon"
                          />
                          {t('header.navigation.delete_catalog')}
                        </Link>
                      </li>
                      <li>
                        <Link to="/add-documents">
                          <FontAwesomeIcon
                            icon={faFileUpload}
                            className="submenu-icon"
                          />
                          {t('header.navigation.add_document')}
                        </Link>
                      </li>
                      <li>
                        <Link to="/manage-documents">
                          <FontAwesomeIcon
                            icon={faTrash}
                            className="submenu-icon"
                          />
                          {t('header.navigation.delete_document')}
                        </Link>
                      </li>
                    </ul>
                  )}
                </li>
              )}
            </ul>

            {/* Desktop Search Container */}
            <div className="search-container">
              <FontAwesomeIcon icon={faSearch} className="search-icon" />
              <input
                key={`search-${currentLanguage}`} // Force re-render when language changes
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder={t('header.search.placeholder')}
              />

              {/* Display Search Results */}
              {searchResults.length > 0 && (
                <div className="search-results" ref={searchResultsRef}>
                  {searchResults.map((product) => {
                    // Handle cases where product.images[0] is a full URL or a base filename
                    let imageUrl = "";
                    const imageEntry = product.images[0];

                    if (imageEntry.startsWith("http")) {
                      imageUrl = imageEntry;
                    } else {
                      imageUrl = constructImageUrl(imageEntry);
                    }

                    const { title, description } = getDisplayText(product);

                    return (
                      <div
                        key={product._id}
                        className="search-result-item"
                        onClick={(e) => handleProductClick(product._id, e)}
                        style={{ cursor: "pointer" }}
                      >
                        <img
                          src={imageUrl}
                          alt={title}
                          className="search-result-image"
                        />
                        <div>
                          <h4>{title}</h4>
                          <p>{description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </nav>
        </div>
      </div>

      {showPopup && (
        <div className="logout-popup">{t('auth.logout_success')}</div>
      )}
    </header>
  );
};

export default Header;
